/* eslint-disable max-lines */
//
//  TokenUtilPreviewMetadataGenerator.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { GradientType } from "../../exports";
import { BorderStyle } from "../../model/enums/SDKBorderStyle";
import { TextCase } from "../../model/enums/SDKTextCase";
import { TextDecoration } from "../../model/enums/SDKTextDecoration";
import { TokenType } from "../../model/enums/SDKTokenType";
import { VisibilityType } from "../../model/enums/SDKVisibilityType";
import { ColorToken } from "../../model/tokens/SDKColorToken";
import { DimensionToken, } from "../../model/tokens/SDKDimensionToken";
import { TokenUtils } from "../../utils/TokenUtils";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Utility
const appendPercentageIfApplicable = (measure, stringToAppendTo) => {
    if (measure && measure < 1) {
        return stringToAppendTo.concat(` / ${Math.round(measure * 100)}%`);
    }
    return stringToAppendTo;
};
const getFullPath = (token) => {
    if (token.tokenPath) {
        return [...token.tokenPath, token.name];
    }
    return undefined;
};
export class TokenUtilPreviewSegmentGenerator {
    static getTypographyValueReferenceProperties(tokenValue, allTokens) {
        var _a, _b, _c, _d;
        if (!tokenValue) {
            return {
                referencedName: undefined,
            };
        }
        const { referencedTokenId } = tokenValue;
        if (!referencedTokenId) {
            return {
                referencedName: undefined,
            };
        }
        const reference = TokenUtils.lookupReference(referencedTokenId, allTokens);
        if (!reference) {
            return {
                referencedName: undefined,
            };
        }
        return {
            referencedName: TokenUtilPreviewSegmentGenerator.getReferenceName(reference),
            referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(reference),
            referenceOriginRemote: !!((_a = reference.origin) === null || _a === void 0 ? void 0 : _a.referenceOriginRemote),
            referenceOriginName: (_c = (_b = reference.origin) === null || _b === void 0 ? void 0 : _b.referenceOriginName) !== null && _c !== void 0 ? _c : undefined,
            referenceFullPath: (_d = getFullPath(reference)) === null || _d === void 0 ? void 0 : _d.join("/"),
        };
    }
    static getReferenceName(reference) {
        const fullPath = reference.tokenPath
            ? [...reference.tokenPath, reference.name]
            : undefined;
        // if the reference.name is only a number (e.g. 40 or 1000), take the name from originName (but just last two parts of the path)
        if (reference.name && /^\d+$/.test(reference.name) && fullPath) {
            return fullPath.slice(-2).join("/");
        }
        return reference.name;
    }
    static getCommonProperties(token, reference) {
        var _a, _b, _c;
        if (!token) {
            return {
                referenceName: undefined,
            };
        }
        const { isTokenOutOfRange, errorMessage: tokenOutOfRangeErrorMessage } = TokenUtils.isTokenOutOfRange(token);
        const { isDifferentTypeValue, errorMessage: tokenDifferentTypeErrorMessage, } = TokenUtils.isDifferentTypeValue(token);
        const referenceFullPath = reference && getFullPath(reference);
        return {
            referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
            referenceOriginRemote: !!((_a = token.origin) === null || _a === void 0 ? void 0 : _a.referenceOriginRemote),
            referenceOriginName: (_c = (_b = token.origin) === null || _b === void 0 ? void 0 : _b.referenceOriginName) !== null && _c !== void 0 ? _c : undefined,
            referenceFullPath: referenceFullPath === null || referenceFullPath === void 0 ? void 0 : referenceFullPath.join("/"),
            referencedName: reference ? this.getReferenceName(reference) : undefined,
            isDifferentTypeValue,
            isValueOutOfRange: isTokenOutOfRange,
            tokenOutOfRangeErrorMessage,
            tokenDifferentTypeErrorMessage,
        };
    }
    static resolvedSegmentsForToken(token, allTokens) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13;
        switch (token.tokenType) {
            case TokenType.color: {
                const t = token;
                const reference = TokenUtils.lookupReference((_a = t.value) === null || _a === void 0 ? void 0 : _a.referencedTokenId, allTokens);
                const opacityReference = TokenUtils.lookupReference((_c = (_b = t.value) === null || _b === void 0 ? void 0 : _b.opacity) === null || _c === void 0 ? void 0 : _c.referencedTokenId, allTokens);
                let segments = [
                    {
                        value: ColorToken.colorValueToHex6(t.value).toUpperCase(),
                        prefersReferenceName: true,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
                if (t.value.opacity.measure !== 1) {
                    segments = segments.concat([
                        "separator",
                        {
                            value: `${Math.round(t.value.opacity.measure * 100)}%`,
                            prefersReferenceName: false,
                            referencedName: (_d = opacityReference === null || opacityReference === void 0 ? void 0 : opacityReference.name) !== null && _d !== void 0 ? _d : undefined,
                        },
                    ]);
                }
                return segments;
            }
            case TokenType.blur: {
                const t = token;
                const reference = TokenUtils.lookupReference((_e = t.value) === null || _e === void 0 ? void 0 : _e.referencedTokenId, allTokens);
                if (reference) {
                    return [
                        {
                            value: `${t.value.type.toLowerCase()} ${DimensionToken.valueToString(t.value.radius)}`,
                            prefersReferenceName: true,
                            ...this.getCommonProperties(token, reference),
                        },
                    ];
                }
                return [
                    {
                        value: (_f = t.value) === null || _f === void 0 ? void 0 : _f.type.toLowerCase(),
                        prefersReferenceName: false,
                        referencedName: undefined,
                    },
                    {
                        value: DimensionToken.valueToString(t.value.radius),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.shadow: {
                const t = token;
                if (t.value.length === 1 && t.value[0]) {
                    return TokenUtilPreviewSegmentGenerator.shadowValuesToSegments(t.value[0], allTokens);
                }
                // For multiple shadows, we just show the number of layers
                return [
                    {
                        value: t.value.length > 0
                            ? `${t.value.length} shadow layers`
                            : `No shadow layers`,
                        prefersReferenceName: false,
                        referencedName: undefined,
                    },
                ];
            }
            case TokenType.gradient: {
                const t = token;
                const layer = t.value[0];
                let type = "";
                if (layer) {
                    switch (layer.type) {
                        case GradientType.linear:
                            type = "linear";
                            break;
                        case GradientType.angular:
                            type = "angular";
                            break;
                        case GradientType.radial:
                            type = "radial";
                            break;
                        default:
                            throw new Error(`Unknown gradient type`);
                    }
                }
                return [
                    {
                        value: t.value.length > 1 ? `${t.value.length} gradient layers` : type,
                        prefersReferenceName: false,
                        referencedName: undefined,
                    },
                ];
                /*
                if (t.value.length === 1) {
                  const layer = t.value[0]
                  const transform = new TokenTransform()
                  const cssString = transform.gradientTokenToCSS(layer)
        
                  return [
                    {
                      value: cssString,
                      prefersReferenceName: false,
                      referencedName: undefined,
                    },
                  ]
                }
        
                // For multiple gradients, we just show the number of layers
                return [
                  {
                    value:
                      t.value.length > 0
                        ? `${t.value.length} gradient layers`
                        : `No gradient layers`,
                    prefersReferenceName: false,
                    referencedName: undefined,
                  },
                ]
                */
            }
            case TokenType.fontFamily: {
                const t = token;
                const reference = TokenUtils.lookupReference((_g = t.value) === null || _g === void 0 ? void 0 : _g.referencedTokenId, allTokens);
                return [
                    {
                        value: t.value.text,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.fontWeight: {
                const t = token;
                const reference = TokenUtils.lookupReference((_h = t.value) === null || _h === void 0 ? void 0 : _h.referencedTokenId, allTokens);
                return [
                    {
                        value: t.value.text,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.fontSize: {
                const t = token;
                const reference = TokenUtils.lookupReference((_j = t.value) === null || _j === void 0 ? void 0 : _j.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.lineHeight: {
                const t = token;
                const reference = TokenUtils.lookupReference((_k = t.value) === null || _k === void 0 ? void 0 : _k.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.letterSpacing: {
                const t = token;
                const reference = TokenUtils.lookupReference((_l = t.value) === null || _l === void 0 ? void 0 : _l.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.paragraphSpacing: {
                const t = token;
                const reference = TokenUtils.lookupReference((_m = t.value) === null || _m === void 0 ? void 0 : _m.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.opacity: {
                const t = token;
                const reference = TokenUtils.lookupReference((_o = t.value) === null || _o === void 0 ? void 0 : _o.referencedTokenId, allTokens);
                return [
                    {
                        value: !TokenUtils.isTokenOutOfRange(t).isTokenOutOfRange &&
                            !TokenUtils.isDifferentTypeValue(t).isDifferentTypeValue
                            ? `${Math.round(t.value.measure * 100)}%`
                            : DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.border: {
                const t = token;
                let style = "";
                switch (t.value.style) {
                    case BorderStyle.dashed:
                        style = "dashed";
                        break;
                    case BorderStyle.dotted:
                        style = "dotted";
                        break;
                    case BorderStyle.solid:
                        style = "solid";
                        break;
                    case BorderStyle.groove:
                        style = "groove";
                        break;
                    default:
                        break;
                }
                const reference = TokenUtils.lookupReference((_p = t.value) === null || _p === void 0 ? void 0 : _p.referencedTokenId, allTokens);
                let segments = [
                    {
                        value: DimensionToken.valueToString(t.value.width),
                        prefersReferenceName: false,
                        referencedName: (_t = (_s = TokenUtils.lookupReference((_r = (_q = t.value) === null || _q === void 0 ? void 0 : _q.width) === null || _r === void 0 ? void 0 : _r.referencedTokenId, allTokens)) === null || _s === void 0 ? void 0 : _s.name) !== null && _t !== void 0 ? _t : undefined,
                    },
                    {
                        value: style,
                        prefersReferenceName: false,
                        referencedName: undefined,
                    },
                    {
                        value: ColorToken.colorValueToHex6(t.value.color).toUpperCase(),
                        prefersReferenceName: true,
                        referencedName: (_x = (_w = TokenUtils.lookupReference((_v = (_u = t.value) === null || _u === void 0 ? void 0 : _u.color) === null || _v === void 0 ? void 0 : _v.referencedTokenId, allTokens)) === null || _w === void 0 ? void 0 : _w.name) !== null && _x !== void 0 ? _x : undefined,
                    },
                ];
                if (reference) {
                    segments = [
                        {
                            value: appendPercentageIfApplicable(t.value.color.opacity.measure, `${DimensionToken.valueToString((_y = t.value) === null || _y === void 0 ? void 0 : _y.width)} ${style} ${ColorToken.colorValueToHex6(t.value.color).toUpperCase()}`),
                            prefersReferenceName: true,
                            ...this.getCommonProperties(token, reference),
                        },
                    ];
                    return segments;
                }
                if (t.value.color.opacity.measure < 1) {
                    // Only add opacity if it is not 100%
                    segments = segments.concat([
                        "separator",
                        {
                            value: `${Math.round(t.value.color.opacity.measure * 100)}%`,
                            prefersReferenceName: false,
                            referencedName: (_0 = (_z = TokenUtils.lookupReference(t.value.color.opacity.referencedTokenId, allTokens)) === null || _z === void 0 ? void 0 : _z.name) !== null && _0 !== void 0 ? _0 : undefined,
                        },
                    ]);
                }
                return segments;
            }
            case TokenType.radius: {
                const t = token;
                const reference = TokenUtils.lookupReference((_1 = t.value) === null || _1 === void 0 ? void 0 : _1.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.borderWidth: {
                const t = token;
                const reference = TokenUtils.lookupReference((_2 = t.value) === null || _2 === void 0 ? void 0 : _2.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.duration: {
                const t = token;
                const reference = TokenUtils.lookupReference((_3 = t.value) === null || _3 === void 0 ? void 0 : _3.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.zIndex: {
                const t = token;
                const reference = TokenUtils.lookupReference((_4 = t.value) === null || _4 === void 0 ? void 0 : _4.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.textCase: {
                const t = token;
                let value = "";
                switch (t.value.value) {
                    case TextCase.upper:
                        value = "Uppercase";
                        break;
                    case TextCase.lower:
                        value = "Lowercase";
                        break;
                    case TextCase.camel:
                        value = "Capitalize";
                        break;
                    case TextCase.smallCaps:
                        value = "Small caps";
                        break;
                    case TextCase.original:
                        value = "Original";
                        break;
                    default:
                        throw new Error("Unknown text case");
                }
                const reference = TokenUtils.lookupReference((_5 = t.value) === null || _5 === void 0 ? void 0 : _5.referencedTokenId, allTokens);
                return [
                    {
                        value,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.textDecoration: {
                const t = token;
                let value = "";
                switch (t.value.value) {
                    case TextDecoration.original:
                        value = "None";
                        break;
                    case TextDecoration.strikethrough:
                        value = "Strikethrough";
                        break;
                    case TextDecoration.underline:
                        value = "Underline";
                        break;
                    default:
                        throw new Error("Unknown text decoration option");
                }
                const reference = TokenUtils.lookupReference((_6 = t.value) === null || _6 === void 0 ? void 0 : _6.referencedTokenId, allTokens);
                return [
                    {
                        value,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.visibility: {
                const t = token;
                let value = "";
                switch (t.value.value) {
                    case VisibilityType.visible:
                        value = "Visible";
                        break;
                    case VisibilityType.hidden:
                        value = "Hidden";
                        break;
                    default:
                        throw new Error("Unknown visibility option");
                }
                const reference = TokenUtils.lookupReference((_7 = t.value) === null || _7 === void 0 ? void 0 : _7.referencedTokenId, allTokens);
                return [
                    {
                        value,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.productCopy: {
                const t = token;
                const reference = TokenUtils.lookupReference((_8 = t.value) === null || _8 === void 0 ? void 0 : _8.referencedTokenId, allTokens);
                return [
                    {
                        value: t.value.text,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.string: {
                const t = token;
                const reference = TokenUtils.lookupReference((_9 = t.value) === null || _9 === void 0 ? void 0 : _9.referencedTokenId, allTokens);
                return [
                    {
                        value: t.value.text,
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.dimension: {
                const t = token;
                const reference = TokenUtils.lookupReference((_10 = t.value) === null || _10 === void 0 ? void 0 : _10.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.size: {
                const t = token;
                const reference = TokenUtils.lookupReference((_11 = t.value) === null || _11 === void 0 ? void 0 : _11.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.space: {
                const t = token;
                const reference = TokenUtils.lookupReference((_12 = t.value) === null || _12 === void 0 ? void 0 : _12.referencedTokenId, allTokens);
                return [
                    {
                        value: DimensionToken.valueToString(t.value),
                        prefersReferenceName: false,
                        ...this.getCommonProperties(token, reference),
                    },
                ];
            }
            case TokenType.typography: {
                const t = token;
                let lineHeightFragments = [];
                const reference = TokenUtils.lookupReference((_13 = t.value) === null || _13 === void 0 ? void 0 : _13.referencedTokenId, allTokens);
                if (reference) {
                    return [
                        {
                            value: `${DimensionToken.valueToString(t.value.fontSize
                            // @ts-expect-error TS(2345): Argument of type 'LineHeightTokenValue | null' is ... Remove this comment to see the full error message
                            )} / ${DimensionToken.valueToString(t.value.lineHeight)} ${t.value.fontFamily.text} ${t.value.fontWeight.text}`,
                            prefersReferenceName: true,
                            ...this.getCommonProperties(token, reference),
                        },
                    ];
                }
                if (t.value.lineHeight) {
                    lineHeightFragments = [
                        "separator",
                        {
                            value: DimensionToken.valueToString(t.value.lineHeight),
                            prefersReferenceName: false,
                            ...this.getTypographyValueReferenceProperties(t.value.lineHeight, allTokens),
                        },
                    ];
                }
                return [
                    {
                        value: DimensionToken.valueToString(t.value.fontSize),
                        prefersReferenceName: false,
                        ...this.getTypographyValueReferenceProperties(t.value.fontSize, allTokens),
                    },
                    ...lineHeightFragments,
                    {
                        value: t.value.fontFamily.text,
                        prefersReferenceName: false,
                        ...this.getTypographyValueReferenceProperties(t.value.fontFamily, allTokens),
                    },
                    {
                        value: t.value.fontWeight.text,
                        prefersReferenceName: false,
                        ...this.getTypographyValueReferenceProperties(t.value.fontWeight, allTokens),
                    },
                ];
            }
            default:
                throw new Error("Unknown token type");
        }
    }
    static shadowValuesToSegments(layer, allTokens) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (!layer) {
            return [];
        }
        const reference = TokenUtils.lookupReference(layer.referencedTokenId, allTokens);
        const formatDimension = (value, unit) => value === 0 ? `0` : `${DimensionToken.numberToString(value)}${unit}`;
        const getReferencedValuePreview = () => {
            var _a, _b, _c;
            const x = formatDimension(layer.x, "px");
            const y = formatDimension(layer.y, "px");
            const radius = formatDimension(layer.radius, "px");
            const spread = formatDimension(layer.spread, "px");
            const color = ColorToken.colorValueToHex6(layer.color).toUpperCase();
            const value = `${x} ${y} ${radius} ${spread} ${color}`;
            return appendPercentageIfApplicable((_b = (_a = layer === null || layer === void 0 ? void 0 : layer.opacity) === null || _a === void 0 ? void 0 : _a.measure) !== null && _b !== void 0 ? _b : (_c = layer === null || layer === void 0 ? void 0 : layer.color) === null || _c === void 0 ? void 0 : _c.opacity.measure, value);
        };
        if (reference) {
            return [
                {
                    value: getReferencedValuePreview(),
                    prefersReferenceName: true,
                    referencedName: (_a = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _a !== void 0 ? _a : undefined,
                },
            ];
        }
        const colorReference = TokenUtils.lookupReference(layer.color.referencedTokenId, allTokens);
        let segments = [
            {
                value: formatDimension(layer.x, "px"),
                prefersReferenceName: false,
                referencedName: undefined,
            },
            {
                value: formatDimension(layer.y, "px"),
                prefersReferenceName: false,
                referencedName: undefined,
            },
            {
                value: formatDimension(layer.radius, "px"),
                prefersReferenceName: false,
                referencedName: undefined,
            },
            {
                value: formatDimension(layer.spread, "px"),
                prefersReferenceName: false,
                referencedName: undefined,
            },
            {
                value: ColorToken.colorValueToHex6(layer.color).toUpperCase(),
                prefersReferenceName: true,
                referencedName: colorReference &&
                    TokenUtilPreviewSegmentGenerator.getReferenceName(colorReference),
                referenceResolutionFailed: colorReference &&
                    TokenUtils.isReferenceResolutionFailed(colorReference),
                referenceOriginRemote: !!((_b = colorReference === null || colorReference === void 0 ? void 0 : colorReference.origin) === null || _b === void 0 ? void 0 : _b.referenceOriginRemote),
                referenceOriginName: (_d = (_c = colorReference === null || colorReference === void 0 ? void 0 : colorReference.origin) === null || _c === void 0 ? void 0 : _c.referenceOriginName) !== null && _d !== void 0 ? _d : undefined,
            },
        ];
        if ((layer.opacity && layer.opacity.measure < 1) ||
            layer.color.opacity.measure < 1) {
            // Only add opacity if it is not 100%
            segments = segments.concat([
                "separator",
                {
                    value: `${Math.round(((_f = (_e = layer.opacity) === null || _e === void 0 ? void 0 : _e.measure) !== null && _f !== void 0 ? _f : layer.color.opacity.measure) * 100)}%`,
                    prefersReferenceName: false,
                    referencedName: (_j = (_h = TokenUtils.lookupReference((_g = layer.opacity) === null || _g === void 0 ? void 0 : _g.referencedTokenId, allTokens)) === null || _h === void 0 ? void 0 : _h.name) !== null && _j !== void 0 ? _j : undefined,
                },
            ]);
        }
        return segments;
    }
}
